import { useState, useCallback } from 'react';

/**
 * Delays unmount to allow exit animations.
 * Uses a callback pattern to avoid ESLint issues with setState in effects
 * and ref access during render.
 *
 * Usage:
 *   const { shouldRender, isAnimating, onAnimationEnd } = useDelayedUnmount(isOpen);
 *   if (!shouldRender) return null;
 *   return <div className={isAnimating ? 'out' : 'in'} onAnimationEnd={onAnimationEnd} />
 */
export function useDelayedUnmount(isOpen: boolean) {
  const [renderState, setRenderState] = useState<'visible' | 'exiting' | 'hidden'>(
    isOpen ? 'visible' : 'hidden'
  );

  // Derive state from props + internal state
  if (isOpen && renderState === 'hidden') {
    setRenderState('visible');
  }
  if (!isOpen && renderState === 'visible') {
    setRenderState('exiting');
  }

  const onAnimationEnd = useCallback(() => {
    setRenderState(prev => prev === 'exiting' ? 'hidden' : prev);
  }, []);

  const shouldRender = renderState !== 'hidden';
  const isAnimating = renderState === 'exiting';

  return { shouldRender, isAnimating, onAnimationEnd };
}
